import React, {useState, useEffect, useRef} from "react";
import {
    Lock,
    CreditCard,
    Percent,
    Users,
    IdCard,
    MapPin,
    Table,
    Package,
    Store,
    MessageCircle,
    Bell,
    ShoppingBag,
    Star,
    ChevronRight,
    ChevronLeft,
    Search,
    ShieldCheck,
    Tag,
    Fingerprint,
    Clock,
    CalendarOff,
    Save,
    MonitorSmartphone,
    ReceiptText
} from "lucide-react";
import {useNavigate} from "react-router-dom";
import TaxesSettings from "../components/Setting/TaxesSettings";
import ClientTypesSettings from "../components/Setting/ClientTypesSettings";
import EmployeesSettings from "../components/Setting/EmployeesSettings";
import ProfilesSettings from "../components/Setting/ProfilesSettings";
import PaymentsSettings from "../components/Setting/PaymentsSettings";
import AreasSettings from "../components/Setting/AreasSettings";
import TablesSettings from "../components/Setting/TablesSettings";
import UnitsSettings from "../components/Setting/UnitsSettings";
import WhatsAppSettings from "../components/Setting/WhatsAppSettings";
import SocialLinksSettings from "../components/Setting/SocialLinksSettings";
import NotificationsSettings from "../components/Setting/NotificationsSettings";
import SalesChannelsSettings from "../components/Setting/SalesChannelsSettings";
import LicenseSettings from "../components/Setting/LicenseSettings";
import AttendanceSettings from "../components/Setting/AttendanceSettings";
import CalendarHoursSettings from "../components/Setting/Calendar/CalendarHoursSettings";
import RestDaysSettings from "../components/Setting/Calendar/RestDaysSettings";
import PosDevicesSettings from "../components/Setting/PosDevices/PosDevicesSettings";
import ClosingTicketSettings from "../components/Setting/ClosingTicketSettings";
import secureStorage from "../utils/secureStorage";
import {api} from "../config/api";
import Notification from "../components/Common/Notification";
import usePersistedTab from "../hooks/usePersistedTab";
import {ClientPhoto, Field, inputClass} from "../ClientsTable/clientFormUI";
import {FormSection, SectionActionsContext} from "../components/Setting/SettingsUI";
import Button from "../components/Common/Button";

const fieldClass = inputClass(false);

const Settings = () => {
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Paramètres - Dashboard";
    }, []);

    const [activeTab, setActiveTab] = usePersistedTab("tab_settings", "général");
    // Mobile follows the iOS pattern: a full-screen list, then drill into a section.
    const [mobileShowList, setMobileShowList] = useState(true);
    const [search, setSearch] = useState("");
    const [accountInfo, setAccountInfo] = useState(null);
    const [isRestaurant, setIsRestaurant] = useState(false);
    // Calendar setup, salons de beauté only — same gate as the POS's Réglages > Calendrier.
    const [isBeautySalon, setIsBeautySalon] = useState(false);
    const [shopLogo, setShopLogo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);
    const [userEmail, setUserEmail] = useState("");
    // Title-bar slot where the open section renders its actions (see SectionActions)
    const [sectionActionsEl, setSectionActionsEl] = useState(null);

    // Notification state
    const [notification, setNotification] = useState({
        show: false,
        type: '',
        message: ''
    });

    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        if (type !== 'loading') {
            setTimeout(() => {
                setNotification({show: false, type: '', message: ''});
            }, 3000);
        }
    };

    const [shopInfo, setShopInfo] = useState({
        name: "",
        phone: "",
        logo: "",
        if: "",
        ice: "",
        country: "",
        city: ""
    });

    // 🎯 Récupérer les informations du compte au chargement
    useEffect(() => {
        const loadAccountInfo = async () => {
            try {
                const account = await secureStorage.getItem("selectedAccount");
                const email = await secureStorage.getItem("_email");

                if (email) {
                    setUserEmail(email);
                }

                if (account) {
                    setAccountInfo(account);
                    console.log(account);
                    // Vérifier si c'est un restaurant (businessType === "004")
                    setIsRestaurant(account.busniessType === "004");
                    setIsBeautySalon(account.busniessType === "001");

                    // Populate shop info from account
                    setShopInfo({
                        name: account.name || "",
                        phone: account.phone || "",
                        logo: account.logo || "",
                        idFiscal: account.idFiscal || "",
                        cnss: account.cnss || "",
                        ice: account.ice || "",
                        rc: account.rc || "",
                        adresse: account.adresse || "",
                        site: account.site || ""
                    });

                    // Set logo preview if exists - handle both base64 and URL
                    if (account.logo) {
                        setShopLogo(account.logo);
                    }
                }
            } catch (error) {
                console.error("Erreur lors du chargement des informations du compte:", error);
            }
        };

        loadAccountInfo();
    }, []);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleShopInfoChange = (e) => {
        const {name, value} = e.target;
        setShopInfo(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showNotification('error', 'Veuillez sélectionner un fichier image valide');
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                showNotification('error', 'La taille de l\'image ne doit pas dépasser 5MB');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => setShopLogo(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const removeLogo = () => {
        setShopLogo(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const triggerLogoUpload = () => {
        fileInputRef.current?.click();
    };

    const handleSaveShopInfo = async () => {
        try {
            setIsLoading(true);
            showNotification('loading', "Mise à jour des informations...");

            // Create JSON payload
            const payload = {
                name: shopInfo.name,
                phone: shopInfo.phone,
                idFiscal: shopInfo.idFiscal,
                cnss: shopInfo.cnss,
                ice: shopInfo.ice,
                rc: shopInfo.rc,
                adresse: shopInfo.adresse,
                site: shopInfo.site
            };

            // Handle logo - send as base64 if it's a new image
            if (shopLogo && shopLogo.startsWith('data:')) {
                payload.logo = shopLogo;
            }

            // Update account via API using PUT method
            const response = await api.put(`/accounts/${accountInfo.id}`, payload, {
                headers: {
                    'X-account-id': accountInfo.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            // Update local storage with new account info
            const updatedAccount = {...accountInfo, ...response.data};
            await secureStorage.setItem('selectedAccount', updatedAccount);
            setAccountInfo(updatedAccount);

            // Update logo preview - handle both base64 and URL responses
            if (response.data.logo) {
                setShopLogo(response.data.logo);
            }

            showNotification('success', "Informations mises à jour avec succès");
        } catch (error) {
            console.error("Error updating shop info:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur est survenue lors de la mise à jour";
            showNotification('error', errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChange = () => {
        // Navigate to change password page
        navigate('/settings/change-password');
    };

    // Grouped navigation: sidebar on desktop, drill-in list on mobile.
    const settingsGroups = [
        {
            key: "boutique",
            label: "Boutique",
            items: [
                {key: "général", label: "Général", Icon: Store},
                {key: "paiements", label: "Paiements", Icon: CreditCard},
                {key: "taxes", label: "Taxes", Icon: Percent},
                {key: "clientTypes", label: "Types de clients", Icon: Tag},
                {key: "units", label: "Unités", Icon: Package},
                {key: "licence", label: "Licence", Icon: ShieldCheck},
            ],
        },
        {
            key: "equipe",
            label: "Équipe",
            items: [
                {key: "employees", label: "Employés", Icon: Users},
                {key: "profils", label: "Profils", Icon: IdCard},
                {key: "pointage", label: "Pointage", Icon: Fingerprint},
            ],
        },
        {
            key: "caisse",
            label: "Caisse",
            items: [
                {key: "posDevices", label: "Appareils & impression", Icon: MonitorSmartphone},
                {key: "closingTicket", label: "Ticket de clôture", Icon: ReceiptText},
            ],
        },
        ...(isBeautySalon ? [{
            key: "calendrier",
            label: "Calendrier",
            items: [
                {key: "calendarHours", label: "Horaires d'ouverture", Icon: Clock},
                {key: "restDays", label: "Repos des employés", Icon: CalendarOff},
            ],
        }] : []),
        ...(isRestaurant ? [{
            key: "restaurant",
            label: "Restaurant",
            items: [
                {key: "zones", label: "Zones", Icon: MapPin},
                {key: "tables", label: "Tables", Icon: Table},
            ],
        }] : []),
        {
            key: "ventes",
            label: "Ventes & Communication",
            items: [
                {key: "salesChannels", label: "Canaux de vente", Icon: ShoppingBag},
                {key: "whatsapp", label: "WhatsApp", Icon: MessageCircle},
                {key: "socialLinks", label: "Avis & réseaux", Icon: Star},
                {key: "notifications", label: "Notifications", Icon: Bell},
            ],
        },
    ];
    const activeSettingsItem = settingsGroups.flatMap(g => g.items).find(i => i.key === activeTab);

    const query = search.trim().toLowerCase();
    const filteredGroups = settingsGroups
        .map(g => ({...g, items: g.items.filter(i => i.label.toLowerCase().includes(query))}))
        .filter(g => g.items.length > 0);

    const selectTab = (key) => {
        handleTabChange(key);
        setMobileShowList(false);
    };

    const shopInitial = (shopInfo.name || accountInfo?.name || "?").trim().charAt(0).toUpperCase();

    const renderAvatar = (sizeClass, textClass) => shopLogo ? (
        <img src={shopLogo} alt="" className={`${sizeClass} rounded-lg object-cover flex-shrink-0 shadow-sm`}/>
    ) : (
        <span className={`${sizeClass} ${textClass} rounded-lg bg-primary text-white font-bold flex items-center justify-center flex-shrink-0 shadow-sm`}>
            {shopInitial}
        </span>
    );

    const searchField = (
        <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
            <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un réglage"
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-primary shadow-sm transition-all"
            />
        </div>
    );

    const accountTile = (onClick, chevron) => (
        <button
            type="button"
            onClick={onClick}
            className="w-full h-16 flex items-center gap-3 px-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
            {renderAvatar("w-10 h-10", "text-base")}
            <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {shopInfo.name || "Ma boutique"}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{userEmail}</span>
            </span>
            {chevron && <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0"/>}
        </button>
    );

    const groupLabelClass = "px-4 pt-4 pb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400";

    return (
        // The page itself doesn't scroll: on desktop (md:h-screen) the section menu
        // stays fixed; on mobile, once a section is open, the page is the viewport
        // minus the 4rem top bar so the section title bar stays fixed. Only the menu
        // list / the section content scroll.
        <div className={`flex flex-col bg-white dark:bg-gray-900 md:min-h-0 md:h-screen ${
            mobileShowList ? "min-h-screen" : "h-[calc(100dvh-4rem)]"
        }`}>
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
            />

            {/* ── Header — same layout as the catalogue page ── */}
            <header className={`bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 ${mobileShowList ? "" : "hidden md:block"}`}>
                <div className="px-4 py-4 sm:px-8">
                    <h1 className="page-title md:text-center">Paramètres</h1>
                </div>
            </header>

            <div className="flex flex-col md:flex-row flex-1 min-h-0">
                {/* Mobile: list of sections; tapping one drills into it. */}
                {mobileShowList && (
                    <div className="md:hidden">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">{searchField}</div>

                        {!query && (
                            <div className="border-b border-gray-200 dark:border-gray-700">
                                {accountTile(() => selectTab("général"), true)}
                            </div>
                        )}

                        {filteredGroups.map(group => (
                            <div key={group.key}>
                                <p className={groupLabelClass}>{group.label}</p>
                                <div className="border-y border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                                    {group.items.map(({key, label, Icon}) => (
                                        <button key={key} type="button" onClick={() => selectTab(key)}
                                                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                                            <Icon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0"/>
                                            <span className="flex-1">{label}</span>
                                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {filteredGroups.length === 0 && (
                            <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">Aucun résultat</p>
                        )}
                    </div>
                )}

                {/* Desktop sidebar — a fixed-height flex sibling next to an independently
                    scrolling content pane (position: sticky can't be used here since the
                    app's page-transition wrapper keeps a permanent CSS transform on an
                    ancestor, which breaks sticky positioning). */}
                <aside className="hidden md:flex md:flex-col w-60 lg:w-64 flex-shrink-0 md:min-h-0 border-r border-gray-200 dark:border-gray-700">
                    <div className="h-16 flex flex-col justify-center px-3 border-b border-gray-200 dark:border-gray-700">{searchField}</div>
                    {!query && (
                        <div className="border-b border-gray-200 dark:border-gray-700">
                            {accountTile(() => handleTabChange("général"), false)}
                        </div>
                    )}

                    <nav className="md:overflow-y-auto md:min-h-0 pb-4">
                        {filteredGroups.map(group => (
                            <div key={group.key}>
                                <p className={groupLabelClass}>{group.label}</p>
                                {group.items.map(({key, label, Icon}) => {
                                    const isActive = activeTab === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => handleTabChange(key)}
                                            aria-current={isActive ? "page" : undefined}
                                            className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left border-l-2 transition-colors ${
                                                isActive
                                                    ? "border-primary bg-primary/5 dark:bg-primary/10 text-primary font-medium"
                                                    : "border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                                            }`}
                                        >
                                            <Icon className="w-4 h-4 flex-shrink-0"/>
                                            <span className="flex-1 truncate">{label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        ))}

                        {filteredGroups.length === 0 && (
                            <p className="px-4 pt-4 text-sm text-gray-500 dark:text-gray-400">Aucun résultat</p>
                        )}
                    </nav>
                </aside>

                <div className={`flex-1 min-w-0 min-h-0 overflow-y-auto ${mobileShowList ? "hidden md:block" : ""}`}>
                {/* Section title bar — on mobile it carries the back button; sticky so it
                    stays in place while the section content scrolls under it. */}
                <div className="sticky top-0 z-20 bg-white dark:bg-gray-900 h-14 md:h-16 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            type="button"
                            onClick={() => setMobileShowList(true)}
                            aria-label="Retour aux paramètres"
                            className="md:hidden -ml-1 p-1 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                            <ChevronLeft className="w-5 h-5"/>
                        </button>
                        {activeSettingsItem && (
                            <h2 className="flex items-center gap-2 text-base sm:text-lg font-semibold text-gray-800 dark:text-white truncate">
                                <activeSettingsItem.Icon className="w-5 h-5 text-primary flex-shrink-0"/>
                                {activeSettingsItem.label}
                            </h2>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {activeTab === "général" && (
                            <Button variant="plain" size="sm" icon={Save} loading={isLoading} onClick={handleSaveShopInfo}>
                                Enregistrer
                            </Button>
                        )}
                        <div ref={setSectionActionsEl} className="flex items-center gap-2 empty:hidden"/>
                    </div>
                </div>

                <SectionActionsContext.Provider value={sectionActionsEl}>

                {activeTab === "général" && (
                    <div className="max-w-4xl p-4 sm:p-6 space-y-6">
                        <ClientPhoto
                            image={shopLogo}
                            initials={shopInitial === "?" ? "" : shopInitial}
                            onPick={triggerLogoUpload}
                            onRemove={removeLogo}
                        />
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="hidden"
                        />

                        <FormSection title="Boutique">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field id="shop-name" label="Nom">
                                    <input id="shop-name" type="text" name="name" placeholder="Nom de la boutique"
                                           className={fieldClass} value={shopInfo.name} onChange={handleShopInfoChange}/>
                                </Field>
                                <Field id="shop-phone" label="Téléphone">
                                    <input id="shop-phone" type="tel" name="phone" placeholder="Téléphone"
                                           className={fieldClass} value={shopInfo.phone} onChange={handleShopInfoChange}/>
                                </Field>
                                <Field id="shop-address" label="Adresse">
                                    <input id="shop-address" type="text" name="adresse" placeholder="Adresse"
                                           className={fieldClass} value={shopInfo.adresse} onChange={handleShopInfoChange}/>
                                </Field>
                                <Field id="shop-site" label="Site Web">
                                    <input id="shop-site" type="text" name="site" placeholder="https://"
                                           className={fieldClass} value={shopInfo.site} onChange={handleShopInfoChange}/>
                                </Field>
                            </div>
                        </FormSection>

                        <FormSection
                            title="Informations légales"
                            description="Ces identifiants apparaissent sur vos factures et documents officiels."
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field id="shop-if" label="Identifiant Fiscal (IF)">
                                    <input id="shop-if" type="text" name="idFiscal" placeholder="Non renseigné"
                                           className={fieldClass} value={shopInfo.idFiscal} onChange={handleShopInfoChange}/>
                                </Field>
                                <Field id="shop-ice" label="ICE">
                                    <input id="shop-ice" type="text" name="ice" placeholder="Non renseigné"
                                           className={fieldClass} value={shopInfo.ice} onChange={handleShopInfoChange}/>
                                </Field>
                                <Field id="shop-rc" label="Registre de Commerce">
                                    <input id="shop-rc" type="text" name="rc" placeholder="Non renseigné"
                                           className={fieldClass} value={shopInfo.rc} onChange={handleShopInfoChange}/>
                                </Field>
                                <Field id="shop-cnss" label="CNSS">
                                    <input id="shop-cnss" type="text" name="cnss" placeholder="Non renseigné"
                                           className={fieldClass} value={shopInfo.cnss} onChange={handleShopInfoChange}/>
                                </Field>
                            </div>
                        </FormSection>

                        <FormSection title="Connexion et sécurité" description="L'email de connexion n'est pas modifiable.">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                                <Field id="shop-email" label="Email">
                                    <input id="shop-email" type="email" value={userEmail} readOnly disabled
                                           className={`${fieldClass} opacity-70 cursor-not-allowed`}/>
                                </Field>
                                <div>
                                    <Button variant="tinted" icon={Lock} onClick={handlePasswordChange}>
                                        Modifier le mot de passe
                                    </Button>
                                </div>
                            </div>
                        </FormSection>
                    </div>
                )}

                {activeTab === "taxes" && (
                    <TaxesSettings/>
                )}

                {activeTab === "clientTypes" && (
                    <ClientTypesSettings/>
                )}

                {activeTab === "employees" && (
                    <EmployeesSettings/>
                )}

                {activeTab === "profils" && (
                    <ProfilesSettings/>
                )}

                {activeTab === "pointage" && (
                    <AttendanceSettings/>
                )}

                {activeTab === "calendarHours" && isBeautySalon && (
                    <CalendarHoursSettings/>
                )}

                {activeTab === "restDays" && isBeautySalon && (
                    <RestDaysSettings/>
                )}

                {activeTab === "posDevices" && (
                    <PosDevicesSettings/>
                )}

                {activeTab === "closingTicket" && (
                    <ClosingTicketSettings/>
                )}

                {activeTab === "paiements" && (
                    <PaymentsSettings/>
                )}

                {/* 🎯 Afficher Zones seulement pour les restaurants */}
                {activeTab === "zones" && isRestaurant && (
                    <AreasSettings/>
                )}

                {/* 🎯 Afficher Tables seulement pour les restaurants */}
                {activeTab === "tables" && isRestaurant && (
                    <TablesSettings/>
                )}

                {activeTab === "units" && (
                    <UnitsSettings/>
                )}

                {activeTab === "licence" && (
                    <LicenseSettings/>
                )}

                {activeTab === "salesChannels" && (
                    <SalesChannelsSettings/>
                )}

                {activeTab === "whatsapp" && (
                    <WhatsAppSettings/>
                )}

                {activeTab === "socialLinks" && (
                    <SocialLinksSettings/>
                )}

                {activeTab === "notifications" && (
                    <NotificationsSettings/>
                )}
                </SectionActionsContext.Provider>
                </div>
            </div>
        </div>
    );
};

export default Settings;
